// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  AlertIcon,
  Flex,
  Loading,
  Typography,
} from '@iheartjane/reefer';
import { QRCode } from 'react-qrcode-logo';

import { useKioskSpacing } from '@jane/kiosk/hooks';

const qrWidth = 240;

type QRCodeCalloutType = 'agreement' | 'jane-gold' | 'link-bank' | 'login';

const getTextAndSubtext = (type: QRCodeCalloutType) => {
  switch (type) {
    // Copy names the agreement being scanned, so the caller supplies it.
    case 'agreement':
      return { text: undefined, subtext: undefined };
    case 'jane-gold':
      return {
        text: 'Learn more about Jane Gold rewards',
        subtext:
          'Scan the QR code to learn more about how Jane Gold could help you get the best price.',
      };
    case 'link-bank':
      return {
        text: 'Scan the QR code with your phone to link your bank account',
        subtext: undefined,
      };
    case 'login':
      return {
        text: 'Scan the QR code and use your phone to sign in',
        subtext:
          'You are logging onto a shared device. To protect your privacy, the kiosk will automatically sign you out after a period of inactivity. You can also sign out manually at any time.',
      };
  }
};

export interface QRCodeCalloutProps {
  ariaLabel?: string;
  /** Omit to render no button — for callers with their own pinned footer. */
  buttonLabel?: string;
  error?: string | null;
  onClick?: () => void;
  qrCode?: string;
  size?: number;
  /** Overrides the copy `type` supplies. */
  text?: string;
  textClassName?: string;
  /** Copy treatment — `body` for a caption rather than a callout title. */
  textVariant?: 'body-regular' | 'h2-semibold';
  type: QRCodeCalloutType;
  url?: string;
}

export const QRCodeCallout = ({
  ariaLabel = 'QR Code',
  buttonLabel,
  error,
  onClick,
  qrCode,
  size = qrWidth,
  text: textOverride,
  textClassName,
  textVariant = 'h2-semibold',
  type,
  url,
}: QRCodeCalloutProps) => {
  const { colors } = useTheme();
  const { defaultSpacing, isIpad, getKioskSpacing } = useKioskSpacing();
  const { text: defaultText, subtext } = getTextAndSubtext(type);
  const text = textOverride ?? defaultText;

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      m={getKioskSpacing(32, 40)}
      width="432px"
      data-url={url}
    >
      {error ? (
        <>
          <Box p={24} ariaLabel="Error Icon">
            <AlertIcon color="error-dark" size="xl" />
          </Box>
          <Typography
            variant="h2-semibold"
            textAlign="center"
            mt={defaultSpacing}
          >
            Something went wrong
          </Typography>
          <Typography textAlign="center" mt={getKioskSpacing(24, 8)}>
            {error}
          </Typography>
        </>
      ) : (
        <>
          <Box
            ariaLabel={ariaLabel}
            // Agreement design pins the QR tight to the header and caption.
            p={type === 'agreement' ? 0 : 24}
            role="img"
          >
            {url ? (
              <QRCode
                id={`mobile-${type}-qr`}
                fgColor={colors.grays.black}
                logoImage={qrCode}
                logoWidth={size * 0.3}
                size={size}
                value={url}
              />
            ) : (
              <Box position="relative" mt={24}>
                <Loading size="lg" />
              </Box>
            )}
          </Box>
          {text && (
            <Typography
              className={textClassName}
              variant={textVariant}
              textAlign="center"
              mt={defaultSpacing}
            >
              {text}
            </Typography>
          )}
          {subtext && (
            <Typography textAlign="center" mt={getKioskSpacing(24, 8)}>
              {subtext}
            </Typography>
          )}
        </>
      )}
      {buttonLabel && (
        <Button
          mt={defaultSpacing}
          label={
            isIpad ? (
              buttonLabel
            ) : (
              <Typography color="text-info" variant="h4-semibold" as="p">
                {buttonLabel}
              </Typography>
            )
          }
          onClick={onClick}
          full
          branded
          size={isIpad ? 'small' : 'large'}
        />
      )}
    </Flex>
  );
};
